import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout, { LegalList, LegalSection } from '../../components/legal/LegalPageLayout';
import {
  COMPANY,
  LEGAL_LAST_UPDATED,
  PRICE_BY_CONTENTS_SENTENCE,
  SIGNUP_CLOSED_DATE,
  SUPPORT_EMAIL,
} from '../../constants/legal';

// 課金・解約・返金の条項は実装挙動と一致させること（#38 の受け入れ基準）。
//   - 当期末まで利用可 : Subscription.cancel_at_period_end / current_period_end を立て、
//                        認可は Entitlement.valid_to で判定する（backend/modules/db/models.py:204）
//   - 日割り返金なし   : 返金額を保持するフィールドは課金スキーマに存在しない
//   - 価格（税込）     : backend/modules/billing/pricing.py の PRICE_JPY_TAX_INCLUDED
// ⚠️ 準拠法・管轄裁判所は所在地（東京都港区）に基づく既定値。法務確認が取れ次第確定させる。

function TermsPage() {
  return (
    <LegalPageLayout title="利用規約" lastUpdated={LEGAL_LAST_UPDATED}>
      <Typography sx={{ fontSize: 14, lineHeight: 1.9, color: 'rgba(216, 236, 250, 0.86)' }}>
        本利用規約（以下「本規約」といいます）は、{COMPANY.name}
        （以下「当社」といいます）が提供する「{COMPANY.serviceName}
        」（以下「本サービス」といいます）のご利用条件を定めるものです。本サービスをご利用いただく場合、本規約に同意いただいたものとみなします。
      </Typography>

      <LegalSection heading="第1条（適用）">
        <LegalList
          items={[
            '本規約は、本サービスの利用に関する当社と利用者との間の一切の関係に適用されます。',
            '当社が本サービス上で掲載する個別の注意事項・案内は、本規約の一部を構成します。',
            '本規約の内容と個別の注意事項の内容が異なる場合は、個別の注意事項の定めが優先します。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第2条（定義）">
        <LegalList
          items={[
            '「利用者」とは、本規約に同意のうえ本サービスを利用する方を指します。',
            '「コンテンツ」とは、中央競馬・地方競馬・競輪の各予測情報を指します。',
            `「有料プラン」とは、利用者が選択したコンテンツについて月額料金を支払う契約を指します。有料プランの新規の申込みは、${SIGNUP_CLOSED_DATE}をもって受付を終了しました。`,
            '当社は、利用者の課金の有無にかかわらず、予想ページ（中央競馬・地方競馬・競輪の各レース一覧および各レースの予測情報）を無料で公開します。それ以外の機能（過去のモデルによる配信、予測データの一括取得等）は、有料プランの利用者に限り提供します。',
            '有料プランの利用者は、当社が本サービスに広告を掲載した場合も、広告を表示せずに本サービスを利用できます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第3条（登録・アカウント）">
        <LegalList
          items={[
            '利用者は、真実かつ正確な情報を登録するものとします。',
            '利用者は、登録したメールアドレスおよびパスワードを自己の責任で管理し、第三者に利用させてはなりません。',
            'アカウントを用いて行われた行為は、当該アカウントの利用者による行為とみなします。',
            '当社は、登録情報に虚偽がある場合その他本規約に違反した場合、事前の通知なくアカウントの利用を停止または削除することができます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第4条（料金および支払方法）">
        <LegalList
          items={[
            PRICE_BY_CONTENTS_SENTENCE,
            '支払方法はクレジットカードのみとします。',
            'お申込み時に初回の料金をご請求し、以後は毎月同日に自動でご請求いたします。',
            '決済処理は当社が委託する決済代行会社を通じて行われます。利用者は、当該決済代行会社の定める条件にも従うものとします。',
            'クレジットカードの有効期限切れ、限度額超過その他の事由により決済ができない場合、当社は本サービスの提供を停止することができます。',
            '料金の支払に必要な通信費および振込手数料等は、利用者の負担とします。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第5条（契約期間および自動更新）">
        <LegalList
          items={[
            '有料プランの契約期間は1ヶ月とし、利用者が解約の手続をとらない限り、同一条件で自動的に更新されます。',
            '次回のご請求日は、マイページで確認できます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第6条（解約）">
        <LegalList
          items={[
            '利用者は、マイページからいつでも解約の手続を行うことができます。',
            '解約の手続を行った場合、次回以降の自動更新は停止します。',
            '解約後も、既にお支払いいただいた期間の末日までは本サービスをご利用いただけます。当該期間の末日をもって閲覧権限は終了します。',
            '解約にあたり、日割りその他の方法による返金は行いません。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第7条（返金）">
        <LegalList
          items={[
            '本サービスはデジタル役務の提供であり、決済完了後ただちに利用が可能となるため、料金の返金はいたしません。',
            '当社は、いわゆる返金保証を設けておりません。',
            '前2項にかかわらず、当社の責めに帰すべき事由により本サービスを長期間提供できなかった場合の取扱いは、当社と利用者が協議のうえ定めます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第8条（本サービスの変更・中断・終了）">
        <LegalList
          items={[
            '当社は、本サービスの内容を予告なく変更することがあります。',
            '当社は、システムの保守、障害、天災その他やむを得ない事由がある場合、事前の通知なく本サービスの全部または一部を中断することができます。',
            '当社は、相当な予告期間をもって利用者に通知することにより、本サービスを終了することができます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第9条（禁止事項）">
        <Typography sx={{ mb: 1 }}>利用者は、次の行為を行ってはなりません。</Typography>
        <LegalList
          items={[
            '本サービスで得た情報を、当社の許可なく複製、転載、販売、配信その他の方法で第三者に提供すること。',
            'アカウントを第三者と共有し、または貸与・譲渡すること。',
            '自動化された手段により本サービスへ過度なアクセスを行い、または情報を機械的に収集すること。',
            'リバースエンジニアリングその他の方法で本サービスの解析を試みること。',
            '法令または公序良俗に違反する行為、および当社もしくは第三者の権利を侵害する行為。',
            'その他、当社が本サービスの運営上不適切と判断する行為。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第10条（免責事項）">
        <LegalList
          items={[
            '本サービスは競馬および競輪に関する参考情報の提供を目的とするものであり、的中、利益、回収率その他の成果を保証するものではありません。',
            '本サービスは投資助言または金融商品取引法上の助言を行うものではありません。',
            '勝馬投票券および車券の購入は、利用者ご自身の判断と責任において行ってください。当社は、購入の結果として生じた損失について責任を負いません。',
            'オッズその他の情報は外部から取得しており、通信状況や市場要因により遅延または変動する場合があります。投票前には必ず公式サイトで最終の情報をご確認ください。',
            '法令により、20歳未満の方は勝馬投票券および車券を購入することができません。',
            '当社が利用者に対して負う損害賠償の額は、当社に故意または重大な過失がある場合を除き、当該損害が発生した月に利用者が当社に支払った料金の額を上限とします。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第11条（知的財産権）">
        <LegalList
          items={[
            '本サービスに含まれる予測情報、指数、文章、画像、プログラムその他一切の情報に関する知的財産権は、当社または正当な権利者に帰属します。',
            '利用者は、本サービスで得た情報を私的な利用の範囲を超えて使用することはできません。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第12条（個人情報の取扱い）">
        <Typography>
          当社は、利用者の個人情報を、別途定めるプライバシーポリシーに従って適切に取り扱います。
        </Typography>
      </LegalSection>

      <LegalSection heading="第13条（本規約の変更）">
        <LegalList
          items={[
            '当社は、必要と判断した場合、本規約を変更することができます。',
            '本規約を変更する場合、当社は変更後の内容および効力発生日を本サービス上に表示します。',
            '変更後の効力発生日以降に本サービスをご利用いただいた場合、変更後の本規約に同意いただいたものとみなします。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第14条（準拠法および管轄裁判所）">
        <LegalList
          items={[
            '本規約の解釈および適用は、日本法を準拠法とします。',
            '本サービスに関して当社と利用者との間に紛争が生じた場合、東京地方裁判所を第一審の専属的合意管轄裁判所とします。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="附則">
        <Typography>
          {`本規約の改定（有料プランの新規申込みの受付終了、予想ページの無料公開、有料プランの利用者への広告非表示）は、${SIGNUP_CLOSED_DATE}から効力を生じます。`}
        </Typography>
      </LegalSection>

      <LegalSection heading="お問い合わせ">
        <Typography>
          本規約に関するお問い合わせは、{SUPPORT_EMAIL} までメールでご連絡ください。
        </Typography>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default TermsPage;
